import { Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SupabaseService } from '../supabase/supabase.service';
import type { Country } from './country.model';

const STORAGE_KEY = 'liciya.country';
const DEFAULT_CODE = 'CO';

/**
 * País activo de licitaciones.
 *
 * Su efecto principal es inyectar `--liciya-h` en <html>: toda la paleta se
 * deriva de ese único número, así que cambiar de país re-tematiza la aplicación
 * completa sin tocar CSS de componente. Al ser OKLCH, el contraste se mantiene
 * en cualquier hue, en claro y en oscuro.
 */
@Injectable({ providedIn: 'root' })
export class CountryService {
  private readonly supabase = inject(SupabaseService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly all = signal<Country[]>([]);
  private readonly selectedCode = signal<string | null>(null);

  readonly countries = computed(() => this.all().filter((country) => country.enabled));

  readonly active = computed<Country | null>(() => {
    const list = this.countries();
    if (list.length === 0) return null;

    const code = this.selectedCode() ?? this.stored() ?? DEFAULT_CODE;
    return list.find((country) => country.code === code) ?? list[0];
  });

  constructor() {
    effect(() => {
      const country = this.active();
      if (!country || !this.isBrowser) return;

      document.documentElement.style.setProperty('--liciya-h', String(country.brand_hue));
      localStorage.setItem(STORAGE_KEY, country.code);
    });
  }

  async load(): Promise<void> {
    // Durante la extraccion de rutas de la build de produccion los tokens del
    // entorno aun no estan sustituidos: no hay a donde consultar.
    if (!this.supabase.isConfigured) return;

    const { data, error } = await this.supabase.client
      .from('countries')
      .select('*')
      .eq('enabled', true)
      .order('name');

    if (error || !data) return;
    this.all.set(data);
  }

  select(code: string): void {
    if (!this.countries().some((country) => country.code === code)) return;
    this.selectedCode.set(code);
  }

  /** Solo para pruebas: evita depender de la red. */
  setCountriesForTest(list: Country[]): void {
    this.all.set(list);
  }

  private stored(): string | null {
    if (!this.isBrowser) return null;
    return localStorage.getItem(STORAGE_KEY);
  }
}
